import { useQuery, useQueryClient, useMutation, queryOptions } from '@tanstack/react-query';
import type { ILocalStorageConfigurationObject } from '../types/ILocalStorage';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { getCookie } from '@intigral/utils/lib/browser-storage/cookie';
import { CACHE_CONFIG_TYPES, COOKIE_NAMES } from '@intigral/constant';
import React from 'react';

// React Query keys for configuration
export const CONFIGURATION_KEYS = {
    configuration: ['configuration'] as const,
    version: ['configuration', 'version'] as const,
    baseConfiguration: ['configuration', 'base'] as const,
} as const;

/**
 * Helper function to extract API key from localStorage or cookies
 * @returns API key string or null if not found
 */
const getApiKeyFromStorage = (): string | null => {
    const userLocalStorage = get(CACHE_CONFIG_TYPES.user);
    if (userLocalStorage?.apiKey) {
        return userLocalStorage.apiKey;
    }

    const userLocalSession: any = getCookie(COOKIE_NAMES.USER_SESSION);
    if (userLocalSession) {
        const userObject: any = window.atob(decodeURIComponent(userLocalSession));
        const { a: sessionApiKey } = JSON.parse(userObject);
        return sessionApiKey;
    }

    return null;
};

/** Pre-login / guest landing: static app key used by language & data-domain bootstrap */
const getApiKeyForConfiguration = (): string | null => {
    return getApiKeyFromStorage() || process.env.NX_PUBLIC_WCP_STATIC_API_KEY || null;
};

// Global QueryClient reference for accessing React Query cache from anywhere
let globalQueryClient: any = null;

/**
 * Initialize global query client reference
 * Call this from your app initialization
 */
export const initializeGlobalQueryClient = async (queryClient: any) => {
    globalQueryClient = queryClient;

    // Also initialize the data domain configuration service
    // Note: The data domain service initialization is now handled directly by the data domain library
};

export const getGlobalQueryClient = () => globalQueryClient;

// Note: Configuration is now saved directly by useDataDomainContainer hook

/**
 * @deprecated Use `useConfiguration()` hook instead for proper React Query integration
 *
 * Global accessor to get configuration from React Query cache
 * This function is called 69+ times throughout the app
 *
 * ⚠️ ANTI-PATTERN: Direct cache access bypasses React Query benefits
 * TODO: Migrate all usages to useConfiguration() hook
 */
export const getConfiguration = (): ILocalStorageConfigurationObject => {
    if (!globalQueryClient) {
        return {} as ILocalStorageConfigurationObject;
    }

    const config = globalQueryClient.getQueryData(CONFIGURATION_KEYS.configuration);

    if (!config) {
        return {} as ILocalStorageConfigurationObject;
    }

    // Return properly typed configuration object
    return config as ILocalStorageConfigurationObject;
};

/**
 * @deprecated Use `useConfigurationVersion()` hook instead
 *
 * Global accessor to get configuration version from React Query cache
 */
export const getConfigurationVersion = (): string | null => {
    if (!globalQueryClient) {
        return null;
    }
    const version = globalQueryClient.getQueryData(CONFIGURATION_KEYS.version);
    return version || null;
};

/**
 * @deprecated Use `useBaseConfiguration()` hook instead
 *
 * Global accessor to get base configuration from React Query cache
 */
export const getBaseConfiguration = (): any | null => {
    if (!globalQueryClient) {
        return null;
    }

    const baseConfig = globalQueryClient.getQueryData(CONFIGURATION_KEYS.baseConfiguration);
    return baseConfig || null;
};

/**
 * Query options factory for configuration
 * Follows TanStack Query v5 best practices for reusable query definitions
 *
 * ✅ SINGLE SOURCE OF TRUTH for configuration loading
 * This is the ONLY place in the codebase that fetches /env endpoint
 * No other code should modify the configuration cache
 *
 * This design enables future refactoring where methods receive config as parameters
 * instead of accessing global state
 *
 * @param extraParams - Additional query options to merge
 * @returns Query options for configuration fetch
 */
export const configurationQueryOptions = (extraParams = {}) => {
    const apiKey = getApiKeyForConfiguration();

    return queryOptions({
        queryKey: CONFIGURATION_KEYS.configuration,
        queryFn: async (): Promise<ILocalStorageConfigurationObject> => {
            if (!apiKey) {
                throw new Error('No API key found in storage');
            }

            const baseUrl = process.env['NX_PUBLIC_GATEWAY_API_BASE_URL'];
            const cmsConfigUrl = `${baseUrl}/v2/${apiKey}/env?deviceType=pc&platform=browser`;

            const response = await fetch(cmsConfigUrl);
            if (!response.ok) {
                throw new Error(`Failed to load configuration: ${response.status} ${response.statusText}`);
            }

            const responseData = await response.json();

            // ✅ API response structure: { data: { data: { qitaf, tvodConfig, ... } } }
            const configuration = responseData?.data?.data || responseData?.data || responseData;

            return configuration;
        },
        enabled: !!apiKey, // Only run when we have an API key
        staleTime: Infinity, // Configuration doesn't change during session
        gcTime: Infinity, // Keep in memory forever
        refetchOnWindowFocus: false,
        retry: 1, // Retry once on failure
        ...extraParams, // Allow overriding options
    });
};

/**
 * Hook to access application configuration
 *
 * ✅ This is the SINGLE source of truth for configuration data
 * Fetches configuration from /env endpoint using proper TanStack Query pattern
 * Replaces both localStorage and Redux config state
 *
 * @param extraParams - Additional query options to override defaults
 * @returns React Query result with configuration data
 */
export const useConfiguration = (extraParams = {}) => {
    return useQuery(configurationQueryOptions(extraParams));
};

/**
 * Hook to access specific configuration sections
 * Usage: const { paidVsFree, operators, profile } = useConfigurationSections();
 *
 * ⚠️ REFACTORED: Reduced default fallbacks to expose data loading issues
 * Only critical defaults are kept to prevent crashes
 *
 * ✅ LOGIN PAGE: Returns safe defaults when configuration is not available (no API key)
 */
export const useConfigurationSections = () => {
    const { data: config, status } = useConfiguration();

    // On login page (no API key), query is disabled and config is undefined
    // Provide empty object to prevent crashes
    const safeConfig = config || ({} as ILocalStorageConfigurationObject);

    return {
        // CRITICAL defaults that prevent crashes
        paidVsFree: safeConfig?.paidVsFree || { lockIconEnable: false },
        operators: safeConfig?.operators || {},
        profile: safeConfig?.profile || { colors: [], profileNameMaxLength: 20, defaultSettings: {} },

        // Non-critical properties - let them be undefined/null to expose issues
        ppvConfig: safeConfig?.ppvConfig,
        ppvConfigV2: safeConfig?.ppvConfigV2,
        ppvEventConfig: safeConfig?.ppvEventConfig,
        tierConfig: safeConfig?.tierConfig,
        consentManagement: safeConfig?.consentManagement,
        promotions: safeConfig?.promotions,
        promotionProviderImages: safeConfig?.promotionProviderImages,
        autoTrailerCarousel: safeConfig?.autoTrailerCarousel,
        player: safeConfig?.player,
        tvodConfig: safeConfig?.tvodConfig,
        fuseJsOptions: safeConfig?.fuseJsOptions,
        kidsConfig: safeConfig?.kidsConfig,
        deviceTranslations: safeConfig?.deviceTranslations,
        gamificationConfigs: safeConfig?.gamificationConfigs,
        upgradedBannerModal: safeConfig?.upgradedBannerModal,
        upgraded_banner_fields: safeConfig?.upgraded_banner_fields,
        promoAutoTrailer: safeConfig?.promoAutoTrailer,
        promoBannerLiveStream: safeConfig?.promoBannerLiveStream,
        layoutConfig: safeConfig?.layoutConfig,
        addonsSubscription: safeConfig?.addonsSubscription,
        geolocationRestriction: safeConfig?.geolocationRestriction,
        extraSubscriptionsGuids: safeConfig?.extraSubscriptionsGuids,
        WatchlistCarousel: safeConfig?.WatchlistCarousel,
        channelImage: safeConfig?.channelImage,
        freeVsPaid: safeConfig?.freeVsPaid,
        video: safeConfig?.video,
        otpWaitTime: safeConfig?.otpWaitTime,
        imagizer: safeConfig?.imagizer,
        default_screen: safeConfig?.default_screen,
        api: safeConfig?.api,
        braze: safeConfig?.braze,
        avodConfigs: safeConfig?.avodConfigs,
        iap_operators: safeConfig?.iap_operators || [],

        // Full config object with minimal critical defaults only
        config: {
            ...safeConfig,
            qitaf: safeConfig?.qitaf,
            // Keep critical playback default to prevent player crashes
            global: safeConfig?.global || {
                playback: { bookmarkInterval: 10 },
            },
            promoSlider: safeConfig?.promoSlider,
        } as ILocalStorageConfigurationObject,
    };
};

/**
 * Hook to access configuration version from React Query cache
 * Version is populated by Data Domain initialization
 */
export const useConfigurationVersion = () => {
    return useQuery({
        queryKey: CONFIGURATION_KEYS.version,
        queryFn: async (): Promise<string> => {
            return '';
        },
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

/**
 * Hook to access base configuration from React Query cache
 * Base configuration is populated by Data Domain initialization
 */
export const useBaseConfiguration = () => {
    return useQuery({
        queryKey: CONFIGURATION_KEYS.baseConfiguration,
        queryFn: async () => {
            return {};
        },
        staleTime: Infinity, // Never goes stale since it's populated by Data Domain
        gcTime: Infinity, // Keep in memory forever
        refetchOnWindowFocus: false,
        retry: false, // Don't retry since Data Domain should handle fetching
    });
};

/**
 * Mutation to force refresh configuration
 * This will trigger Data Domain to re-initialize and refresh React Query cache
 */
export const useRefreshConfiguration = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            // Clear React Query cache to force re-initialization
            queryClient.removeQueries({ queryKey: ['configuration'] });
            queryClient.removeQueries({ queryKey: ['data-domain'] });

            return true;
        },
        onSuccess: () => {
            // Invalidate data-domain query to trigger re-initialization
            queryClient.invalidateQueries({ queryKey: ['data-domain'] });
        },
    });
};

/**
 * Hook to get specific configuration values with type safety
 */
export const useConfigurationValue = <T = any>(
    selector: (config: ILocalStorageConfigurationObject) => T,
    defaultValue?: T
) => {
    const { data: configuration, ...rest } = useConfiguration();

    const value = configuration ? selector(configuration) : defaultValue;

    return {
        data: value,
        configuration,
        ...rest,
    };
};

/**
 * Utility hook to get country code from configuration
 */
export const useCountryCode = () => {
    const { data } = useConfigurationValue((config) => {
        // Import the getCountry function from helpers to match current behavior
        const country = globalThis?.navigator?.language?.split('-')[1]?.toLowerCase() || 'us';
        return (config as any)?.countryCodeThreeLettersMapping?.[country] || null;
    }, null);

    return data;
};

/**
 * Utility hook to check if configuration is loading
 */
export const useConfigurationStatus = () => {
    const configQuery = useConfiguration();
    const versionQuery = useConfigurationVersion();

    return {
        isLoading: configQuery.isLoading || versionQuery.isLoading,
        isError: configQuery.isError || versionQuery.isError,
        error: configQuery.error || versionQuery.error,
        isSuccess: configQuery.isSuccess && versionQuery.isSuccess,
        isStale: configQuery.isStale || versionQuery.isStale,
    };
};
